import { useState, type ReactNode } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { MarginBar } from '@/components/margin-bar';
import { PriceEquation } from '@/components/price-equation';
import { ProfitBadge } from '@/components/profit-badge';
import { RecordToolbar, SaidaComAlteracoesDialog } from '@/components/record-toolbar';
import { Button } from '@/components/ui/button';

/** Vitrine dos componentes de apresentacao (so em DEV). Valores de exemplo do design, como strings. */
export const Route = createFileRoute('/_dev/componentes')({
  beforeLoad: () => {
    // Vitrine so em dev, ou em build feito para o e2e (VITE_VITRINE=1). Nunca no build de producao.
    if (!import.meta.env.DEV && import.meta.env.VITE_VITRINE !== '1') throw notFound();
  },
  component: Componentes,
});

const SAVED_AT = new Date(2026, 9, 17, 12, 24);

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section aria-label={titulo} className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <h2 className="text-title font-semibold">{titulo}</h2>
      {children}
    </section>
  );
}

function Caso({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-caption font-medium text-ink-secondary">{rotulo}</div>
      {children}
    </div>
  );
}

function Componentes() {
  const [saidaAberta, setSaidaAberta] = useState(false);
  const noop = () => undefined;
  const comum = {
    index: 2,
    total: 10294,
    canPrev: true,
    canNext: true,
    onList: noop,
    onSave: noop,
    onNew: noop,
    onUndo: noop,
    onPrev: noop,
    onNext: noop,
    onDelete: noop,
    onDuplicate: noop,
    onPrint: noop,
  };

  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="text-small text-ink-muted">Desenvolvimento / Componentes</div>
        <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Componentes</h1>
      </header>

      <Secao titulo="RecordToolbar">
        <Caso rotulo="Sem alterações (salvo às 12:24)">
          <RecordToolbar fixa={false} {...comum} dirty={false} savedAt={SAVED_AT} />
        </Caso>
        <Caso rotulo="Alterado (2 campos)">
          <RecordToolbar fixa={false} {...comum} dirty alteradosCount={2} />
        </Caso>
        <Caso rotulo="Alterado sem contagem">
          <RecordToolbar fixa={false} {...comum} dirty />
        </Caso>
        <Caso rotulo="Salvando">
          <RecordToolbar fixa={false} {...comum} dirty saving alteradosCount={2} />
        </Caso>
        <Caso rotulo="Erro ao salvar">
          <RecordToolbar fixa={false} {...comum} dirty erro="Preço fora da vigência da tabela" alteradosCount={2} />
        </Caso>
        <Caso rotulo="Novo registro">
          <RecordToolbar fixa={false} {...comum} dirty={false} novo index={undefined} total={undefined} canPrev={false} canNext={false} />
        </Caso>
        <Caso rotulo="Primeiro registro">
          <RecordToolbar fixa={false} {...comum} dirty={false} savedAt={SAVED_AT} index={1} canPrev={false} />
        </Caso>
        <Caso rotulo="Último registro">
          <RecordToolbar fixa={false} {...comum} dirty={false} savedAt={SAVED_AT} index={10294} total={10294} canNext={false} />
        </Caso>
        <Caso rotulo="Tema escuro (sem alterações)">
          <div className="rounded-2xl bg-white p-5">
            <RecordToolbar fixa={false} {...comum} variant="dark" dirty={false} savedAt={SAVED_AT} />
          </div>
        </Caso>
        <Caso rotulo="Tema escuro (alterado)">
          <div className="rounded-2xl bg-white p-5">
            <RecordToolbar fixa={false} {...comum} variant="dark" dirty alteradosCount={2} />
          </div>
        </Caso>
        <Caso rotulo="Saída com alterações">
          <Button variant="outline" onClick={() => setSaidaAberta(true)}>
            Abrir confirmação
          </Button>
          <SaidaComAlteracoesDialog
            bloqueado={saidaAberta}
            sair={() => setSaidaAberta(false)}
            ficar={() => setSaidaAberta(false)}
          />
        </Caso>
      </Secao>

      <Secao titulo="ProfitBadge">
        <div className="flex flex-wrap gap-3">
          <Caso rotulo="Na meta (igual)">
            <ProfitBadge lucro="0.2000" meta="0.20" minimo="0.05" />
          </Caso>
          <Caso rotulo="Acima da meta">
            <ProfitBadge lucro="0.2184" meta="0.20" minimo="0.05" />
          </Caso>
          <Caso rotulo="Entre mínimo e meta">
            <ProfitBadge lucro="0.1200" meta="0.20" minimo="0.05" />
          </Caso>
          <Caso rotulo="Abaixo do mínimo">
            <ProfitBadge lucro="0.0300" meta="0.20" minimo="0.05" />
          </Caso>
          <Caso rotulo="Valor inválido">
            <ProfitBadge lucro="" meta="0.20" minimo="0.05" />
          </Caso>
        </div>
      </Secao>

      <Secao titulo="PriceEquation">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(420px,100%),1fr))] gap-5">
          <PriceEquation
            titulo="Preço sugerido"
            badge={<ProfitBadge lucro="0.2000" meta="0.20" minimo="0.05" />}
            custoUnitario="6.23"
            markup="3.5385"
            markupNota="3,3636 × IPI 1,052"
            aVista="22.03"
            custoFinanceiro="1.42"
            custoFinanceiroNota="6,46% · 30 dias"
            preco="23.45"
            semIpi="22.29"
            ipi="1.16"
          />
          <PriceEquation
            titulo={
              <>
                Preço praticado <span className="font-normal text-ink-muted">· simulação</span>
              </>
            }
            badge={<ProfitBadge lucro="0.2184" meta="0.20" minimo="0.05" />}
            rotuloMarkup="Markup implícito"
            rotuloPreco="Preço praticado"
            custoUnitario="6.23"
            markup="3.7717"
            markupNota="+0,2332 sobre o sugerido"
            aVista="23.48"
            custoFinanceiro="1.52"
            custoFinanceiroNota="6,46% · 30 dias"
            preco="25.00"
            semIpi="23.76"
            ipi="1.24"
          />
        </div>
      </Secao>

      <Secao titulo="MarginBar">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(420px,100%),1fr))] gap-5">
          <Caso rotulo="Sugerido (lucro 20%)">
            <MarginBar
              segmentos={[
                { chave: 'custo', rotulo: 'Custo', fracao: '0.3100', valor: '7.27' },
                { chave: 'variavel', rotulo: 'Desp. variável', fracao: '0.2900', valor: '6.80' },
                { chave: 'fixa', rotulo: 'Desp. fixa', fracao: '0.2000', valor: '4.69' },
                { chave: 'lucro', rotulo: 'Lucro', fracao: '0.2000', valor: '4.69' },
              ]}
            />
          </Caso>
          <Caso rotulo="Praticado (lucro 21,84%)">
            <MarginBar
              segmentos={[
                { chave: 'custo', rotulo: 'Custo', fracao: '0.2900', valor: '7.25' },
                { chave: 'variavel', rotulo: 'Desp. variável', fracao: '0.2800', valor: '7.00' },
                { chave: 'fixa', rotulo: 'Desp. fixa', fracao: '0.2116', valor: '5.29' },
                { chave: 'lucro', rotulo: 'Lucro', fracao: '0.2184', valor: '5.46' },
              ]}
            />
          </Caso>
          <Caso rotulo="Lucro negativo (largura 0, valor visível)">
            <MarginBar
              segmentos={[
                { chave: 'custo', rotulo: 'Custo', fracao: '0.4500', valor: '10.55' },
                { chave: 'variavel', rotulo: 'Desp. variável', fracao: '0.3300', valor: '7.74' },
                { chave: 'fixa', rotulo: 'Desp. fixa', fracao: '0.2680', valor: '6.28' },
                { chave: 'lucro', rotulo: 'Lucro', fracao: '-0.0480', valor: '-1.12' },
              ]}
            />
          </Caso>
          <Caso rotulo="Tudo zero">
            <MarginBar
              segmentos={[
                { chave: 'custo', rotulo: 'Custo', fracao: '0', valor: '0' },
                { chave: 'variavel', rotulo: 'Desp. variável', fracao: '0', valor: '0' },
                { chave: 'fixa', rotulo: 'Desp. fixa', fracao: '0', valor: '0' },
                { chave: 'lucro', rotulo: 'Lucro', fracao: '0', valor: '0' },
              ]}
            />
          </Caso>
        </div>
      </Secao>
    </div>
  );
}
